import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Send,
  Notifications,
  Person,
  Science,
  CheckCircle,
  Error,
  Info,
} from '@mui/icons-material';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

interface NotificationHistory {
  id: number;
  title: string;
  body: string;
  user_id: number;
  user_name: string;
  sent_at: string;
  status: 'sent' | 'failed' | 'pending';
}

export const AdminNotificationsPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    title: '',
    body: '',
    data: {} as any,
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user?.is_admin) {
      // Load notification history in background (non-blocking)
      loadNotificationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllUsers();
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationHistory = async () => {
    try {
      // This would need to be implemented in the backend
      // For now, we'll use a mock implementation
      setNotificationHistory([]);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load notification history:', err);
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!formData.user_id || !formData.title || !formData.body) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await apiService.sendNotification({
        user_id: parseInt(formData.user_id),
        title: formData.title,
        body: formData.body,
        data: formData.data,
      });

      setSuccess('Notification sent successfully!');
      setOpenDialog(false);
      resetForm();
      loadNotificationHistory();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      setLoading(true);
      await apiService.testNotification();
      setSuccess('Test notification sent successfully!');
      setTestDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToAllUsers = async () => {
    if (!formData.title || !formData.body) {
      setError('Please fill in title and body');
      return;
    }

    try {
      setLoading(true);
      await apiService.sendNotificationToAllUsers({
        title: formData.title,
        body: formData.body,
        data: formData.data,
      });

      setSuccess('Notification sent to all users successfully!');
      setOpenDialog(false);
      resetForm();
      loadNotificationHistory();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send notification to all users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      title: '',
      body: '',
      data: {},
    });
  };

  const handleOpenDialog = async () => {
    resetForm();
    setOpenDialog(true);
    // Lazy load users only when dialog is opened
    if (users.length === 0) {
      await loadUsers();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Error color="error" />;
      case 'pending':
        return <Info color="info" />;
      default:
        return <Notifications />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!user?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You need admin privileges to access this section.</Typography>
        </Alert>
      </Box>
    );
  }

  // Don't block the UI with loading spinner - show content immediately

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Notification Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Send notifications to users and manage notification history.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Science />}
            onClick={() => setTestDialogOpen(true)}
          >
            Test Notification
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleOpenDialog}
          >
            Send Notification
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        {/* Send Notification Card */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<Send />}
                  onClick={handleOpenDialog}
                  fullWidth
                >
                  Send Custom Notification
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Science />}
                  onClick={() => setTestDialogOpen(true)}
                  fullWidth
                >
                  Send Test Notification
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Notification Stats */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification Statistics
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Total Sent:</Typography>
                  <Typography variant="h6">{notificationHistory.length}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Successful:</Typography>
                  <Typography color="success.main">
                    {notificationHistory.filter(n => n.status === 'sent').length}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Failed:</Typography>
                  <Typography color="error.main">
                    {notificationHistory.filter(n => n.status === 'failed').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Notification History */}
        <Box sx={{ flex: '1 1 100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification History
              </Typography>
              {notificationHistory.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Notifications sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No notifications sent yet
                  </Typography>
                  <Typography color="text.secondary">
                    Send your first notification to see the history here.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {notificationHistory.map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(notification.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1">
                                {notification.title}
                              </Typography>
                              <Chip
                                label={notification.status}
                                color={getStatusColor(notification.status) as any}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                To: {notification.user_name}
                              </Typography>
                              <Typography variant="body2">
                                {notification.body}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Sent: {new Date(notification.sent_at).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Send Notification Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Recipient</InputLabel>
              <Select
                value={formData.user_id}
                label="Recipient"
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person />
                      {user.name} ({user.email})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Message"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              multiline
              rows={4}
              fullWidth
              required
            />
            <TextField
              label="Additional Data (JSON)"
              value={JSON.stringify(formData.data)}
              onChange={(e) => {
                try {
                  const data = JSON.parse(e.target.value);
                  setFormData({ ...formData, data });
                } catch {
                  // Invalid JSON, keep the text but don't update data
                }
              }}
              multiline
              rows={2}
              fullWidth
              helperText="Optional JSON data to include with the notification"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSendToAllUsers} variant="outlined" disabled={loading}>
            {loading ? 'Sending...' : 'Send to All Users'}
          </Button>
          <Button onClick={handleSendNotification} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send to Selected User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Notification Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Notification</DialogTitle>
        <DialogContent>
          <Typography>
            This will send a test notification to verify that the notification system is working correctly.
            The test notification will be sent to your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTestNotification} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
